import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Package, MapPin, User, CreditCard, 
  Calendar, Box, FileText, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Truck, Activity, RotateCcw, ScanBarcode
} from "lucide-react";

const PharmeasyShipmentDetail = ({ user, onLogout }) => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingEvents, setFetchingEvents] = useState(false);

  useEffect(() => {
    fetchShipment();
  }, [shipmentId]);

  const fetchShipment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/shipments/${shipmentId}`);
      setShipment(res.data);
    } catch (e) {
      toast.error("Failed to load shipment details");
      navigate("/pharmeasy/orders");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchEvents = async () => {
    setFetchingEvents(true);
    try {
      await axios.post(`${API}/pharmeasy-orders/shipments/${shipmentId}/fetch-events`);
      toast.success("Events fetched successfully");
      fetchShipment(); // Refresh to show new events
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch events");
    } finally {
      setFetchingEvents(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "—";
    return `₹${Number(val).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      CREATED: "bg-slate-100 text-slate-700",
      READY_FOR_DELIVERY: "bg-blue-100 text-blue-700",
      SORTED: "bg-cyan-100 text-cyan-700",
      BAGGED: "bg-indigo-100 text-indigo-700",
      DISPATCHED: "bg-purple-100 text-purple-700",
      RECEIVED: "bg-orange-100 text-orange-700",
      REACHED_LM_HUB: "bg-amber-100 text-amber-700",
      OUT_FOR_DELIVERY: "bg-yellow-100 text-yellow-800",
      DELIVERED: "bg-emerald-100 text-emerald-700",
      CANCELLED: "bg-red-100 text-red-700",
      RTO_DELIVERED: "bg-rose-100 text-rose-700",
      REMOVED: "bg-gray-100 text-gray-600",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Loading shipment details...</div>
        </div>
      </PharmeasyLayout>
    );
  }

  if (!shipment) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Shipment not found</div>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="shipment-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/pharmeasy/orders")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-800">Shipment Details</h1>
              {shipment.detail_fetched ? (
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Details Fetched
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <Clock className="w-3.5 h-3.5 mr-1" /> Pending Details
                </Badge>
              )}
            </div>
            <p className="text-slate-500 mt-1">ID: {shipment.shipmentId}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={shipment.paymentType === "COD" ? "bg-orange-100 text-orange-800" : "bg-sky-100 text-sky-800"}>
              {shipment.paymentType || "—"}
            </Badge>
            {shipment.priorityType === "HIGH" && (
              <Badge className="bg-red-100 text-red-700">
                <AlertCircle className="w-3 h-3 mr-1" /> HIGH PRIORITY
              </Badge>
            )}
            <Badge variant="outline" className="text-slate-600">
              {shipment.our_status || "UNASSIGNED"}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-emerald-600" /> Shipment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Shipment ID</p>
                  <p className="font-mono font-semibold text-slate-800">{shipment.shipmentId || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">LBN</p>
                  <p className="font-mono text-slate-800">{shipment.lbn || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Reference ID</p>
                  <p className="font-mono text-sm text-slate-700 break-all">{shipment.referenceId || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Package ID</p>
                  <p className="font-mono text-sm text-slate-700">{shipment.packageId || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">AWB</p>
                  <p className="font-mono text-slate-700">{shipment.awb || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Type</p>
                  <Badge variant="outline">{shipment.type || "—"}</Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Service Type</p>
                  <p className="text-slate-700">{shipment.serviceType || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">BOLT Status</p>
                  <Badge variant="outline" className="text-blue-600">{shipment.bolt_status || "—"}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Info Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-blue-600" /> Delivery Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Expected Delivery</p>
                  <p className="text-slate-800">{formatDate(shipment.committedExpectedDeliveryDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">EDD</p>
                  <p className="text-slate-700">{formatDate(shipment.expectedDeliveryDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Imported At</p>
                  <p className="text-slate-700">{formatDate(shipment.imported_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Last Synced</p>
                  <p className="text-slate-700">{formatDate(shipment.last_synced_at)}</p>
                </div>
                {shipment.detail_fetched && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Details Fetched At</p>
                    <p className="text-slate-700">{formatDate(shipment.detail_fetched_at)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Customer Info Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-purple-600" /> Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Recipient Name</p>
                  <p className="text-slate-800 font-medium">
                    {shipment.recipientName || shipment.destAddress?.name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Mobile</p>
                  <p className="text-slate-800 font-mono">
                    {shipment.recipientMobileNo || shipment.destAddress?.contactNumber || "—"}
                  </p>
                </div>
              </div>
              
              {/* Destination Address - handle object format */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase font-medium mb-2">Destination Address</p>
                {shipment.destAddress ? (
                  typeof shipment.destAddress === 'object' ? (
                    <div className="space-y-1">
                      {shipment.destAddress.address1 && (
                        <p className="text-slate-700 text-sm">{shipment.destAddress.address1}</p>
                      )}
                      {shipment.destAddress.address2 && (
                        <p className="text-slate-700 text-sm">{shipment.destAddress.address2}</p>
                      )}
                      {shipment.destAddress.address3 && (
                        <p className="text-slate-600 text-sm">{shipment.destAddress.address3}</p>
                      )}
                      <p className="text-slate-700 text-sm font-medium">
                        {[
                          shipment.destAddress.city,
                          shipment.destAddress.state,
                          shipment.destAddress.pincode
                        ].filter(Boolean).join(", ")}
                      </p>
                      {shipment.destAddress.alternateContactNumber && (
                        <p className="text-slate-600 text-sm">
                          Alt. Contact: {shipment.destAddress.alternateContactNumber}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-700 text-sm whitespace-pre-wrap">{String(shipment.destAddress)}</p>
                  )
                ) : (
                  <p className="text-slate-400 text-sm italic">Address not available (details pending)</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Address Line 1</p>
                  <p className="text-slate-700 text-sm">{shipment.dropAddressLine1 || shipment.destAddress?.address1 || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Address Line 2</p>
                  <p className="text-slate-700 text-sm">{shipment.dropAddressLine2 || shipment.destAddress?.address2 || "—"}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">City</p>
                  <p className="text-slate-800">{shipment.dropCity || shipment.destAddress?.city || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">State</p>
                  <p className="text-slate-700">{shipment.dropState || shipment.destAddress?.state || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Pincode</p>
                  <p className="font-mono text-slate-800">{shipment.dropPincode || shipment.destAddress?.pincode || "—"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment & Value Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-amber-600" /> Payment & Value
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Payment Type</p>
                  <Badge className={shipment.paymentType === "COD" ? "bg-orange-100 text-orange-800" : "bg-sky-100 text-sky-800"}>
                    {shipment.paymentType || "—"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Payment Mode</p>
                  <p className="text-slate-700">{shipment.paymentMode || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Shipment Value</p>
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(shipment.shipmentValue)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Invoice Value</p>
                  <p className="text-lg font-semibold text-slate-700">{formatCurrency(shipment.invoiceValue)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">COD Amount</p>
                  <p className="text-slate-700">{formatCurrency(shipment.codAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weight & Dimensions Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Box className="w-5 h-5 text-teal-600" /> Weight & Dimensions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Weight</p>
                  <p className="text-slate-800 font-medium">{shipment.weight ? `${shipment.weight} kg` : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Volumetric Weight</p>
                  <p className="text-slate-700">{shipment.volumetricWeight ? `${shipment.volumetricWeight} kg` : "—"}</p>
                </div>
              </div>
              {shipment.dimensions && (
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Dimensions (L x B x H)</p>
                  <p className="text-slate-700">
                    {shipment.dimensions.length || "—"} x {shipment.dimensions.breadth || "—"} x {shipment.dimensions.height || "—"} cm
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Source Address Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-rose-600" /> Source Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shipment.srcAddress ? (
                typeof shipment.srcAddress === 'object' ? (
                  <div className="space-y-1">
                    {shipment.srcAddress.name && (
                      <p className="text-slate-800 font-medium">{shipment.srcAddress.name}</p>
                    )}
                    {shipment.srcAddress.address1 && (
                      <p className="text-slate-700 text-sm">{shipment.srcAddress.address1}</p>
                    )}
                    {shipment.srcAddress.address2 && (
                      <p className="text-slate-700 text-sm">{shipment.srcAddress.address2}</p>
                    )}
                    {shipment.srcAddress.address3 && (
                      <p className="text-slate-600 text-sm">{shipment.srcAddress.address3}</p>
                    )}
                    <p className="text-slate-700 text-sm font-medium">
                      {[
                        shipment.srcAddress.city,
                        shipment.srcAddress.state,
                        shipment.srcAddress.pincode
                      ].filter(Boolean).join(", ")}
                    </p>
                    {shipment.srcAddress.contactNumber && (
                      <p className="text-slate-600 text-sm">Contact: {shipment.srcAddress.contactNumber}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-700">{String(shipment.srcAddress)}</p>
                )
              ) : (
                <p className="text-slate-400 italic">Source address not available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Products Section */}
        {shipment.products && shipment.products.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-indigo-600" /> Products ({shipment.products.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Product Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">UCode</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Batch</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Barcode</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Refrigerated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {shipment.products.map((product, idx) => (
                      <tr key={product.id || idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-800">{product.displayName || product.productName || product.name || "—"}</td>
                        <td className="px-4 py-3 align-middle text-sm font-mono text-slate-600">{product.ucode || product.sku || product.skuCode || "—"}</td>
                        <td className="px-4 py-3 align-middle text-sm font-mono text-slate-600">{product.batch || "—"}</td>
                        <td className="px-4 py-3 align-middle text-sm font-mono text-slate-500">{product.barcode || "—"}</td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700 text-right">{product.quantity || product.qty || "—"}</td>
                        <td className="px-4 py-3 align-middle text-sm">
                          {product.refrigerated ? (
                            <Badge className="bg-blue-100 text-blue-700">Yes</Badge>
                          ) : (
                            <span className="text-slate-400">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Info (if any) */}
        {shipment.detail_fetch_error && (
          <Card className="border-0 shadow-lg border-l-4 border-l-red-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                <XCircle className="w-5 h-5" /> Detail Fetch Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-600 text-sm">{shipment.detail_fetch_error}</p>
              <p className="text-slate-500 text-xs mt-2">
                Attempted at: {formatDate(shipment.detail_fetch_attempted_at)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Shipment Events Timeline */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5 text-purple-600" /> 
                Shipment Events 
                {shipment.events?.length > 0 && (
                  <Badge variant="outline" className="ml-2">{shipment.events.length}</Badge>
                )}
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleFetchEvents}
                disabled={fetchingEvents}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="fetch-events-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fetchingEvents ? "animate-spin" : ""}`} />
                {fetchingEvents ? "Fetching..." : "Refresh Events"}
              </Button>
            </div>
            {shipment.events_fetched_at && (
              <p className="text-xs text-slate-400 mt-1">
                Last refreshed: {formatDate(shipment.events_fetched_at)}
                {shipment.events_fetched_by && <span className="ml-1">by <span className="font-medium text-slate-500">{shipment.events_fetched_by}</span></span>}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {!shipment.events || shipment.events.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No events yet</p>
                <p className="text-xs text-slate-400 mt-1">Click "Refresh Events" to fetch the latest status</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                
                <div className="space-y-4">
                  {shipment.events.map((event, idx) => (
                    <div key={event.id || idx} className="relative pl-10">
                      {/* Timeline dot */}
                      <div className={`absolute left-2 w-5 h-5 rounded-full border-2 border-white shadow ${
                        idx === 0 ? "bg-emerald-500" : "bg-slate-300"
                      }`}>
                        {idx === 0 && event.shipmentStatus === "DELIVERED" && (
                          <CheckCircle className="w-3 h-3 text-white absolute top-0.5 left-0.5" />
                        )}
                      </div>
                      
                      <div className={`p-4 rounded-lg ${idx === 0 ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={getStatusColor(event.shipmentStatus)}>
                            {event.shipmentStatus || "UNKNOWN"}
                          </Badge>
                          <span className="text-xs text-slate-500">{formatDate(event.createdOn)}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                          {event.operator && (
                            <div>
                              <span className="text-slate-400">Operator:</span>
                              <span className="ml-1 text-slate-700">{event.operator}</span>
                            </div>
                          )}
                          {event.sprinterName && (
                            <div>
                              <span className="text-slate-400">Rider:</span>
                              <span className="ml-1 text-slate-700">{event.sprinterName}</span>
                            </div>
                          )}
                          {event.assetName && (
                            <div>
                              <span className="text-slate-400">Hub:</span>
                              <span className="ml-1 text-slate-700">{event.assetName}</span>
                            </div>
                          )}
                          {event.bin && (
                            <div>
                              <span className="text-slate-400">Bin:</span>
                              <span className="ml-1 font-mono text-slate-700">{event.bin}</span>
                            </div>
                          )}
                          {event.vehicleNumber && (
                            <div>
                              <span className="text-slate-400">Vehicle:</span>
                              <span className="ml-1 font-mono text-slate-700">{event.vehicleNumber}</span>
                            </div>
                          )}
                          {event.bagId && (
                            <div>
                              <span className="text-slate-400">Bag ID:</span>
                              <span className="ml-1 font-mono text-slate-700">{event.bagId}</span>
                            </div>
                          )}
                          {event.received_by && (
                            <div>
                              <span className="text-slate-400">Received By:</span>
                              <span className="ml-1 text-slate-700">{event.received_by}</span>
                              {event.receivedByRelation && (
                                <span className="text-slate-400"> ({event.receivedByRelation})</span>
                              )}
                            </div>
                          )}
                          {event.tripId && (
                            <div>
                              <span className="text-slate-400">Trip:</span>
                              <span className="ml-1 font-mono text-slate-700">{event.tripId}</span>
                            </div>
                          )}
                        </div>
                        
                        {event.reason && (
                          <div className="mt-2 text-xs text-red-600">
                            Reason: {event.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Events Fetch Error */}
        {shipment.events_fetch_error && (
          <Card className="border-0 shadow-lg border-l-4 border-l-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
                <AlertCircle className="w-5 h-5" /> Events Fetch Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-600 text-sm">{shipment.events_fetch_error}</p>
              <p className="text-slate-500 text-xs mt-2">
                Attempted at: {formatDate(shipment.events_fetch_attempted_at)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Status Reset History */}
        {shipment.status_reset_history?.length > 0 && (
          <Card className="border-0 shadow-lg border-l-4 border-l-orange-400">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
                <RotateCcw className="w-5 h-5" /> Status Reset History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {shipment.status_reset_history.map((h, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${idx === shipment.status_reset_history.length - 1 ? "bg-orange-500" : "bg-slate-300"}`} />
                    <div>
                      <p className="text-sm text-slate-800">
                        <Badge variant="outline" className="text-xs mr-1">{h.from_status}</Badge>
                        <span className="text-slate-400 mx-1">&rarr;</span>
                        <Badge className="bg-slate-100 text-slate-700 text-xs">{h.to_status}</Badge>
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {h.reset_at ? formatDate(h.reset_at) : "—"}
                        {h.reset_by && <span className="ml-2">by {h.reset_by}</span>}
                      </p>
                      {h.reason && <p className="text-xs text-slate-500 mt-0.5">{h.reason}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inward Sessions */}
        {shipment.inward_sessions?.length > 0 && (
          <Card className="border-0 shadow-lg border-l-4 border-l-teal-400" data-testid="inward-sessions-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-teal-700">
                <ScanBarcode className="w-5 h-5" /> Inward Sessions ({shipment.inward_sessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Session Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Trip #</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Bolt Trip ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Inwarded By</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Inwarded At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shipment.inward_sessions.map((is_, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle">
                          {is_.session_id ? (
                            <a
                              href={`/pharmeasy/hub-ops/session/${is_.session_id}`}
                              className="text-blue-600 hover:underline font-medium"
                              data-testid={`inward-session-link-${idx}`}
                            >
                              {is_.session_code || is_.session_id}
                            </a>
                          ) : (
                            <span className="text-slate-500">{is_.session_code || "—"}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle text-slate-500">
                          <div className="whitespace-nowrap">
                            <div>{formatDate(is_.session_created_at).split(",")[0]}</div>
                            <div className="text-xs text-slate-500">{formatDate(is_.session_created_at).split(",")[1]?.trim()}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-slate-700">{is_.trip_number ?? "—"}</td>
                        <td className="px-4 py-3 align-middle font-mono text-slate-600">{is_.bolt_trip_id || "—"}</td>
                        <td className="px-4 py-3 align-middle text-slate-700">{is_.inwarded_by || <span className="text-slate-400 italic">Pending</span>}</td>
                        <td className="px-4 py-3 align-middle text-slate-500">
                          {is_.inwarded_at ? (
                            <div className="whitespace-nowrap">
                              <div>{formatDate(is_.inwarded_at).split(",")[0]}</div>
                              <div className="text-xs text-slate-500">{formatDate(is_.inwarded_at).split(",")[1]?.trim()}</div>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyShipmentDetail;
